import { FC, useCallback } from 'react';
import { Col, Row } from 'reactstrap';

import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings } from '@/types/company-settings';
import config, {
  FormsType,
  forms_show_logo,
  forms_show_text,
  logo,
  logo_text,
  form_export_header_show_linked_manual,
  form_show_header
} from 'views/companies/settings/forms/config';
import {
  EMPTY_SETTING_VALUE,
  getSettingFileId,
  isSettingEmpty
} from 'views/companies/settings/helpers';

import Category from 'views/companies/settings/Category';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import Checkbox from '@/library/components/form/inputs/Checkbox';
import { FieldsStateType } from '@/common/types/form';
import Switch from '@/library/components/form/inputs/Switch';
import { css } from '@emotion/react';
import UploadFiles from '@/library/components/form/inputs/upload-files';
import InputLabel from 'views/companies/settings/components/InputLabel';
import Input from '@/library/components/form/inputs/Input';
import { CATEGORY_IDS } from '../constants';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatBooleanDisplay, formatDisplayValue } from '../change-tracking';

type FormsProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.forms;
const CATEGORY_TITLE = 'Forms';

const LABELS = {
  [logo_text]: 'Logo text',
  [logo]: 'Form export header logo',
  [form_export_header_show_linked_manual]: 'Show linked manual in PDF Export',
  [form_show_header]: 'Show Header and Footer'
};

const Forms: FC<FormsProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { selectField, changeField, setFieldError, ...rest } = useFormState(formState);
  const fields: FieldsStateType<FormsType> = rest.fields;

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    const logoValue = companySettings[logo] as Parameters<typeof isSettingEmpty>[0];
    const logoTextValue = companySettings[logo_text];

    loadValues({
      [logo_text]: isSettingEmpty(logoTextValue) ? '' : logoTextValue,
      [logo]: isSettingEmpty(logoValue) ? null : logoValue,
      [forms_show_logo]: !isSettingEmpty(logoValue),
      [forms_show_text]: !isSettingEmpty(logoTextValue),
      [form_export_header_show_linked_manual]:
        companySettings[form_export_header_show_linked_manual] === 1,
      [form_show_header]: companySettings[form_show_header] === 1
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) => collectValues(validate) as FormsType | null,
    toPayload: values => {
      const logoFileId = values[forms_show_logo] ? getSettingFileId(values[logo]) : null;

      return [
        {
          key: logo_text,
          value:
            values[forms_show_text] && values[logo_text] ? values[logo_text] : EMPTY_SETTING_VALUE
        },
        {
          key: logo,
          value: logoFileId ? [logoFileId] : EMPTY_SETTING_VALUE
        },
        {
          key: form_export_header_show_linked_manual,
          value: values[form_export_header_show_linked_manual] ? '1' : '0'
        },
        {
          key: form_show_header,
          value: values[form_show_header] ? '1' : '0'
        }
      ];
    },
    labels: LABELS,
    formatValue: (key, value) => {
      if (key === form_show_header || key === form_export_header_show_linked_manual) {
        return formatBooleanDisplay(value);
      }
      if (key === logo) {
        return formatDisplayValue(value);
      }
      return formatDisplayValue(value);
    },
    watch: fields,
    validate: values => {
      if (!values[forms_show_logo]) return true;

      if (getSettingFileId(values[logo]) == null) {
        setFieldError(logo, 'Upload a logo or turn off Show logo');
        return false;
      }

      return true;
    }
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <Row className="d-flex align-items-center">
        <Col className="d-flex align-items-center h-24 ps-1">
          <VisibleSetting categoryId={CATEGORY_ID} settingKey={form_show_header}>
            <Checkbox
              label={
                <div>
                  Show Header and Footer{' '}
                  <span className="text-violet fs-10">| form-show-header</span>
                </div>
              }
              labelClassName="fs-12 fw-medium text-primary"
              onChange={() => selectField(form_show_header)(!fields[form_show_header].value)}
              {...fields[form_show_header]}
            />
          </VisibleSetting>
        </Col>
      </Row>

      <Row className="d-flex align-items-center text-violet gx-1 mt-1 fs-12">
        <Col xs={3}>Header - Left Part</Col>
        <Col xs={4} className="ps-4">
          Header - Center Part
        </Col>
        <Col xs={3} className="ps-4">
          Header - Right Part
        </Col>
      </Row>

      <Row className="d-flex align-items-start text-violet gx-1 mt-2 fs-12">
        <Col xs={3}>
          <VisibleSetting categoryId={CATEGORY_ID} settingKey={logo}>
            <Row className="d-flex">
              <Col xs={6} className="d-flex align-items-center h-24">
                <Switch
                  className="mb-0"
                  css={css`
                    .slide-label {
                      text-transform: none;
                    }
                  `}
                  size="sm"
                  label="Show logo"
                  switchClassName="turquoise-switch"
                  labelClassName="cme-12 fs-12 fw-medium user-select-none"
                  value={fields[forms_show_logo].value}
                  onChange={() => selectField(forms_show_logo)(!fields[forms_show_logo].value)}
                />
              </Col>
              {fields[forms_show_logo].value ? (
                <Col xs={6}>
                  <UploadFiles
                    group="filesystem"
                    label={<InputLabel className="text-nowrap" title="ATTACHMENTS" apiKey={logo} />}
                    files={fields[logo]?.value || null}
                    error={fields[logo]?.error}
                    onChange={files => {
                      selectField([logo])(files && files.length ? files[0] : null);
                    }}
                    size="sm"
                    singleUpload={true}
                  />
                </Col>
              ) : null}
            </Row>
          </VisibleSetting>
        </Col>
        <Col xs={4} className="d-flex ps-4 position-relative">
          <VisibleSetting
            categoryId={CATEGORY_ID}
            settingKey={form_export_header_show_linked_manual}
          >
            <Checkbox
              className="mb-0"
              label={
                <InputLabel
                  className="text-nowrap"
                  title="Show linked manual in PDF Export"
                  apiKey={form_export_header_show_linked_manual}
                />
              }
              checkMarkClassName={`${
                fields[form_export_header_show_linked_manual].value
                  ? 'bg-turquoise border-turquoise'
                  : ''
              }`}
              labelClassName="fs-12 fw-medium text-primary"
              onChange={() =>
                selectField(form_export_header_show_linked_manual)(
                  !fields[form_export_header_show_linked_manual].value
                )
              }
              {...fields[form_export_header_show_linked_manual]}
            />
            <div className="position-absolute text-violet fs-12 right-96 pt-1 top-100">
              *We always show the name of the form.
            </div>
          </VisibleSetting>
        </Col>
        <Col xs={4} className="d-flex align-self-start  ps-4">
          <div className="text-violet fs-12 lh-15">
            *If “Show linked manual in PDF Export” is selected, we show here the number of the
            Chapter
          </div>
        </Col>
      </Row>

      <Row className="d-flex align-items-center text-violet gx-1 mt-2 fs-12">
        <Col xs={3}>
          <VisibleSetting categoryId={CATEGORY_ID} settingKey={logo_text}>
            <Row className="d-flex align-items-top">
              <Col xs={6} className="d-flex h-24 cpt-4">
                <Switch
                  className="mb-0"
                  css={css`
                    .slide-label {
                      text-transform: none;
                    }
                  `}
                  size="sm"
                  label="Show text"
                  switchClassName="turquoise-switch"
                  labelClassName="cme-12 fs-12 fw-medium user-select-none"
                  value={fields[forms_show_text].value}
                  onChange={() => selectField(forms_show_text)(!fields[forms_show_text].value)}
                />
              </Col>
              {fields[forms_show_text].value ? (
                <Col xs={4}>
                  <Input
                    className="mb-0"
                    innerClassName="h-24"
                    label={<InputLabel title="LOGO TEXT" apiKey={'logo_text'} />}
                    placeholder="Add text"
                    onChange={changeField(logo_text)}
                    {...fields[logo_text]}
                  />
                </Col>
              ) : null}
            </Row>
          </VisibleSetting>
        </Col>
      </Row>
    </Category>
  );
};

export default Forms;
